import { useCallback, useState } from 'react'
import { Thread } from '../components/chat/Thread'
import { ThreadsSheet } from '../components/chat/ThreadsSheet'
import { useThreads } from '../lib/chat'
import { todaysThread } from '../lib/chatClient'
import '../components/chat/chat.css'

/**
 * WP 9.2: the Ask tab, at `#/butler`, in the slot the Feed held. Mounted
 * like the other four and kept alive on a tab switch (X7), so a turn
 * streaming while Doug checks Today is still there when he comes back.
 *
 * Which thread is on screen: today's by default — the most recent, if it
 * was used today in Berlin — else none, until the first question makes
 * one. *New* and the threads sheet choose explicitly. A choice remounts
 * `Thread` (its `key`), which is what clears a turn in flight from the old
 * thread; the Worker naming the thread a turn went to (`x-thread-id`) is
 * adopted *without* a remount, or the answer streaming in would vanish.
 *
 * Reads only. Nothing here writes: a proposal is a chip that does nothing
 * until 9.4, and history is the Worker's to persist (9.1).
 */
export function Butler() {
  const threadsQ = useThreads()
  /** `undefined`: follow today's thread. `{ id: null }`: a fresh one, by
   *  choice. `{ id }`: that one. Wrapped so "none, by choice" and "not
   *  chosen" are two values rather than one `null` doing both jobs. */
  const [chosen, setChosen] = useState<{ id: string | null } | undefined>(undefined)
  const [generation, setGeneration] = useState(0)
  const [sheet, setSheet] = useState(false)

  const today = todaysThread(threadsQ.data, new Date())
  const threadId = chosen ? chosen.id : today?.id ?? null
  const thread = threadsQ.data?.find(t => t.id === threadId) ?? null

  const choose = (id: string | null) => {
    setChosen({ id })
    setGeneration(g => g + 1)
  }
  const adopt = useCallback((id: string) => setChosen({ id }), [])

  return (
    <>
      <Thread
        key={generation}
        threadId={threadId}
        title={thread?.title ?? null}
        loading={!chosen && threadsQ.isPending}
        listError={!chosen && !threadsQ.data ? threadsQ.error : null}
        fresh={chosen?.id === null}
        onNew={() => choose(null)}
        onThreads={() => setSheet(true)}
        onThreadId={adopt}
      />
      <ThreadsSheet
        open={sheet}
        threads={threadsQ.data ?? []}
        error={threadsQ.data ? null : threadsQ.error}
        current={threadId}
        onPick={id => {
          setSheet(false)
          if (id !== threadId) choose(id)
        }}
        onClose={() => setSheet(false)}
      />
    </>
  )
}
